import React, { useEffect, useState, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

// UsernameEditor - reusable inline editor for the public-profile
// username. Used in two places:
//   1. /settings — as a section on the settings hub (embedded).
//   2. UsernameSettingsModal — the header of the "Public profile"
//      overlay opened from the user dropdown, alongside the OG
//      preview + share buttons.
//
// Handles live availability checking (debounced 350ms), inline
// status pill (checking / available / current / unavailable / error),
// and the PATCH to /api/auth/me/username. The parent decides layout;
// this component owns only the input + status + save button.
export const UsernameEditor = ({
    // When true, hides the Save button and auto-saves on blur once
    // status === 'available'. Not used by default (explicit Save
    // reduces accidental changes).
    autoSaveOnBlur = false,
    // Called after a successful save. Parent typically closes a modal
    // or shows a toast.
    onSaved,
    // Optional test-id prefix so parent screens can distinguish
    // between the modal and settings-page instances.
    testIdPrefix = 'username-editor',
}) => {
    const { user, checkAuth } = useAuth();
    const [draft, setDraft] = useState('');
    const [status, setStatus] = useState({ state: 'idle', message: null, suggestion: null });
    const [saving, setSaving] = useState(false);
    const debounceRef = useRef(null);

    // Initialise draft from the user's current username whenever the
    // authenticated user's handle changes (e.g., after a save cycle
    // refreshes the AuthContext).
    useEffect(() => {
        setDraft(user?.username || '');
        setStatus({ state: 'idle', message: null, suggestion: null });
    }, [user?.username]);

    // Debounced availability check
    useEffect(() => {
        const candidate = draft.trim().toLowerCase();
        if (!candidate) {
            setStatus({ state: 'idle', message: null, suggestion: null });
            return;
        }
        if (candidate === user?.username) {
            setStatus({ state: 'current', message: 'This is your current username.', suggestion: null });
            return;
        }
        setStatus({ state: 'checking', message: 'Checking…', suggestion: null });

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${API}/auth/me/username/check?u=${encodeURIComponent(candidate)}`,
                    { credentials: 'include' },
                );
                if (!res.ok) {
                    setStatus({ state: 'error', message: 'Could not check availability.', suggestion: null });
                    return;
                }
                const data = await res.json();
                if (data.available) {
                    setStatus({ state: 'available', message: data.reason || 'Available!', suggestion: null });
                } else {
                    setStatus({
                        state: 'unavailable',
                        message: data.reason || 'Unavailable.',
                        suggestion: data.suggestion || null,
                    });
                }
            } catch (err) {
                setStatus({ state: 'error', message: 'Network error.', suggestion: null });
            }
        }, 350);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [draft, user?.username]);

    const handleSave = async () => {
        const candidate = draft.trim().toLowerCase();
        if (!candidate || candidate === user?.username) {
            return;
        }
        if (status.state !== 'available') {
            toast.error('Pick an available username first.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API}/auth/me/username`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username: candidate }),
            });
            if (!res.ok) {
                const detail = (await res.json().catch(() => null))?.detail || 'Failed to save username.';
                toast.error(detail);
                return;
            }
            toast.success('Username updated.');
            await checkAuth({ silent: true });
            onSaved?.(candidate);
        } catch (err) {
            toast.error('Could not save username.');
        } finally {
            setSaving(false);
        }
    };

    const handleBlur = () => {
        if (autoSaveOnBlur && status.state === 'available') {
            handleSave();
        }
    };

    const renderStatusIcon = () => {
        switch (status.state) {
            case 'checking':
                return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid={`${testIdPrefix}-status-icon-checking`} />;
            case 'available':
            case 'current':
                return <Check className="w-4 h-4 text-emerald-600" data-testid={`${testIdPrefix}-status-icon-ok`} />;
            case 'unavailable':
            case 'error':
                return <AlertCircle className="w-4 h-4 text-red-500" data-testid={`${testIdPrefix}-status-icon-error`} />;
            default:
                return null;
        }
    };

    const hasChange = draft.trim().toLowerCase() !== (user?.username || '');
    const saveDisabled =
        saving
        || !draft.trim()
        || !hasChange
        || status.state === 'unavailable'
        || status.state === 'checking'
        || status.state === 'error';

    return (
        <div data-testid={testIdPrefix}>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                Username
            </label>
            <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground font-mono">brainsync.pro/</span>
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.toLowerCase())}
                    onBlur={handleBlur}
                    placeholder="your-name"
                    maxLength={24}
                    spellCheck={false}
                    className="font-mono"
                    data-testid={`${testIdPrefix}-input`}
                />
                <div className="w-5 flex-shrink-0">{renderStatusIcon()}</div>
            </div>
            {status.message && (
                <div
                    className={`text-xs mt-1.5 flex items-center gap-1.5 ${
                        status.state === 'available' || status.state === 'current'
                            ? 'text-emerald-700'
                            : status.state === 'unavailable' || status.state === 'error'
                                ? 'text-red-600'
                                : 'text-muted-foreground'
                    }`}
                    data-testid={`${testIdPrefix}-status`}
                >
                    <span>{status.message}</span>
                    {status.suggestion && (
                        <button
                            type="button"
                            onClick={() => setDraft(status.suggestion)}
                            className="font-mono font-semibold underline underline-offset-2 hover:text-emerald-800"
                            data-testid={`${testIdPrefix}-suggestion-btn`}
                        >
                            Try “{status.suggestion}”
                        </button>
                    )}
                </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                3-24 characters · lowercase letters, digits, hyphens · must start with a letter.
            </p>

            {/* Save button only appears once the draft diverges from
                the saved username - keeps the row quiet by default and
                acts as an explicit confirmation before a live change. */}
            {!autoSaveOnBlur && hasChange && (
                <div className="mt-3 flex justify-end">
                    <Button
                        onClick={handleSave}
                        disabled={saveDisabled}
                        size="sm"
                        data-testid={`${testIdPrefix}-save-btn`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save username
                    </Button>
                </div>
            )}
        </div>
    );
};

export default UsernameEditor;
